/**
 * Layout-shaped loading skeleton.
 *
 * Deliberately NOT a generic spinner: each variant mirrors the real layout it
 * precedes — same heading block, same card count, same widths — so when the data
 * lands the content appears in place instead of the page jumping. That only works
 * if the two stay in step: if a screen's structure changes, change its variant
 * here too.
 *
 * Rendered INSIDE the app shell (navbar + max-w-6xl column), so it never carries
 * page chrome of its own — no min-height, no background, no max-width.
 *
 *   variant="list"   → Template list: heading, subtitle, selector, one card, footer
 *   variant="editor" → Template editor: heading, details card, subitems card, footer
 *   variant="usage"  → App Usage: heading, subtitle, the framed dashboard
 */
import React from 'react';

/** One grey bar. `w`/`h` are Tailwind classes so callers stay declarative. */
const Bar = ({ w = 'w-full', h = 'h-4', className = '' }) => (
  <div className={`${w} ${h} bg-track rounded ${className}`} />
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-surface border border-subtle rounded-lg shadow-sm ${className}`}>
    {children}
  </div>
);

/** Table placeholder: header strip then n row strips, matching SubitemsTable. */
const TableSkeleton = ({ rows = 3 }) => (
  <div className="overflow-hidden border border-subtle rounded-md">
    <div className="flex items-center gap-6 px-3 py-3 border-b border-subtle">
      <Bar w="w-6" h="h-3.5" />
      <Bar w="w-24" h="h-3.5" />
      <Bar w="w-20" h="h-3.5" />
      <Bar w="w-24" h="h-3.5" />
      <Bar w="w-28" h="h-3.5" />
    </div>
    {Array.from({ length: rows }, (_, i) => (
      <div
        key={i}
        className={`flex items-center gap-6 px-3 py-3.5 ${
          i < rows - 1 ? 'border-b border-subtle' : ''
        }`}
      >
        <Bar w="w-4" h="h-3" />
        <Bar w="w-28" h="h-3" />
        <Bar w="w-16" h="h-3" />
        <Bar w="w-20" h="h-3" />
        <Bar w="w-24" h="h-3" />
      </div>
    ))}
  </div>
);

function ListSkeleton() {
  return (
    <>
      {/* heading + subtitle */}
      <div className="mb-6">
        <Bar w="w-44" h="h-6" />
        <Bar w="w-96 max-w-full" h="h-4" className="mt-3" />
      </div>

      {/* bare selector — matches the max-w-md unwrapped dropdown */}
      <div className="max-w-md mb-6">
        <Bar w="w-36" h="h-3.5" className="mb-2" />
        <Bar h="h-10" />
      </div>

      {/* the one card: identity + actions, then the table */}
      <Card className="p-6 mb-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <Bar w="w-40" h="h-5" />
            <Bar w="w-56" h="h-3.5" />
          </div>
          <div className="flex flex-none gap-3">
            <Bar w="w-28" h="h-10" />
            <Bar w="w-20" h="h-10" />
            <Bar w="w-24" h="h-10" />
          </div>
        </div>
        <TableSkeleton rows={3} />
      </Card>

      {/* Apply footer */}
      <div className="flex justify-end">
        <Bar w="w-36" h="h-10" />
      </div>
    </>
  );
}

function EditorSkeleton() {
  return (
    <>
      {/* back link, heading, subheading */}
      <div className="mb-6">
        <Bar w="w-36" h="h-4" className="mb-4" />
        <Bar w="w-40" h="h-6" />
        <Bar w="w-2/3" h="h-4" className="mt-3" />
      </div>

      {/* details card: name field + the two access-level rows */}
      <Card className="p-6 mb-6">
        <Bar w="w-32" h="h-3.5" className="mb-2" />
        <div className="max-w-md mb-6">
          <Bar h="h-10" />
        </div>
        <Bar w="w-44" h="h-4" className="mb-3" />
        <div className="space-y-3">
          <Bar h="h-14" />
          <Bar h="h-14" />
        </div>
      </Card>

      {/* subitems card: header row then the table */}
      <Card className="mb-6">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-subtle">
          <Bar w="w-24" h="h-5" />
          <Bar w="w-28" h="h-3.5" />
          <span className="flex-1" />
          <Bar w="w-40" h="h-8" />
        </div>
        <div className="p-4">
          <TableSkeleton rows={4} />
        </div>
      </Card>

      {/* Cancel / Save footer */}
      <div className="flex items-center justify-between">
        <Bar w="w-24" h="h-10" />
        <Bar w="w-36" h="h-10" />
      </div>
    </>
  );
}

/**
 * App Usage: back link, heading, subtitle, then one tall block where the
 * embedded dashboard goes. The height is the same expression AppUsageView gives
 * the iframe, so the page does not resize the moment the frame arrives — keep
 * the two in step.
 */
function UsageSkeleton() {
  return (
    <>
      <div className="mb-6">
        <Bar w="w-36" h="h-4" className="mb-4" />
        <Bar w="w-40" h="h-6" />
        <Bar w="w-80 max-w-full" h="h-4" className="mt-3" />
      </div>

      <Card className="overflow-hidden">
        <div
          className="w-full bg-track"
          style={{ height: 'calc(100vh - 237px)', minHeight: 420 }}
        />
      </Card>
    </>
  );
}

export default function LayoutSkeleton({ variant = 'list', label = 'Loading…' }) {
  return (
    // role=status + aria-busy so assistive tech announces the wait; the bars
    // themselves are decorative, hence the single sr-only label.
    <div role="status" aria-busy="true" aria-live="polite" className="animate-pulse">
      <span className="sr-only">{label}</span>
      {variant === 'editor' ? (
        <EditorSkeleton />
      ) : variant === 'usage' ? (
        <UsageSkeleton />
      ) : (
        <ListSkeleton />
      )}
    </div>
  );
}
